import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { Loader2, Printer } from "lucide-react";
import { printClosing } from "@/lib/printShift";

export default function ShiftCloseModal({ open, onClose, onClosed, storeName }) {
  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState(null);
  const [summary, setSummary] = useState(null);
  const [cash, setCash] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setCash(""); setShift(null); setSummary(null);
    api.get("/shifts/summary")
      .then((r) => { setShift(r.data.shift); setSummary(r.data.summary); })
      .catch((e) => { toast.error(apiError(e)); onClose(); })
      .finally(() => setLoading(false));
  }, [open]);

  const expected = summary?.cash_expected || 0;
  const selisih = (Number(cash || 0)) - expected;

  const doClose = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/shifts/close", { cash_counted: Number(cash || 0) });
      toast.success("Shift ditutup");
      printClosing(data, data.summary, storeName);
      onClosed(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="shift-close-dialog" aria-describedby={undefined} className="bg-ui-card border-ui-line text-ui-text max-w-md">
        <DialogHeader><DialogTitle>Tutup Shift</DialogTitle></DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-readable" /></div>
        ) : summary && (
          <div className="space-y-3">
            <div className="rounded-xl border border-ui-line bg-ui-soft divide-y divide-ui-line text-sm">
              <Row label="Jumlah Transaksi" value={summary.tx_count} mono />
              <Row label="Total Penjualan" value={rupiah(summary.revenue)} mono />
              <Row label="Tunai" value={rupiah(summary.tunai)} mono />
              <Row label="Transfer" value={rupiah(summary.transfer)} mono />
              <Row label="QRIS" value={rupiah(summary.qris)} mono />
              <Row label="Kembalian" value={rupiah(summary.change)} mono />
              <Row label="Piutang (baru)" value={rupiah(summary.piutang)} mono cls="text-danger-readable" />
              <Row label="Pelunasan Piutang" value={rupiah(summary.pelunasan)} mono cls="text-brand-readable" />
              <Row label="Kas Seharusnya" value={rupiah(expected)} mono cls="text-warning-readable font-bold" />
            </div>
            <div>
              <label className="text-xs text-ui-muted">Hitung Fisik Kas (uang di laci)</label>
              <MoneyInput
                data-testid="shift-cash-input"
                value={cash}
                onValueChange={setCash}
                placeholder="0"
                className="mt-1 h-11 text-right font-mono bg-ui-card border-ui-line text-lg"
              />
            </div>
            <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${selisih === 0 ? "bg-brand-soft text-brand-readable" : selisih > 0 ? "bg-accent-soft text-accent-readable" : "bg-danger-soft text-danger-readable"}`}>
              <span className="text-sm font-semibold">Selisih</span>
              <span data-testid="shift-selisih" className="font-mono font-bold">{rupiah(selisih)}</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-ui-line bg-ui-card hover:bg-ui-muted-surface">Batal</Button>
          <Button data-testid="shift-close-confirm" onClick={doClose} disabled={saving || loading} className="bg-danger hover:bg-danger text-ui-text gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Tutup & Cetak
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, mono, cls = "" }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5">
      <span className="text-ui-muted">{label}</span>
      <span className={`${mono ? "font-mono" : ""} ${cls}`}>{value}</span>
    </div>
  );
}
